import { createContext, FC, useCallback, useContext, useMemo } from 'react';
import { SUB_FACILITIES } from 'constants/endpoints';
import { METHOD_DELETE } from 'requests';
import { useAPIActions } from 'store/reducers/api/apiSlice';
import { ApiRequestConfigType } from 'requests/index.types';
import { handleGenericErrorMessage } from 'utils/handleGenericErrorMessage';
import { DeleteCustomerDialogProps } from 'pages/Settings/pages/LocationSettings/CustomersSettingsPage/DeleteCustomerDialog/DeleteCustomerDialog.types';
import { toastService } from 'services';
import { useTranslation } from 'hooks/useTranslation';
import { useParams } from 'react-router-dom';
import { useCustomersSettingsPageDataContext } from 'pages/Settings/pages/LocationSettings/CustomersSettingsPage/CustomersSettingsPage.data.context';

interface DeleteCustomerDialogForm {
  handleDeleteCustomer: () => void;
  customerName?: string;
}

const API_DELETE_SubFacility = (
  community_id: string,
  sub_facility_id: string,
): ApiRequestConfigType => ({
  __typename: 'LegacyAPI',
  url: `${SUB_FACILITIES(community_id)}/${sub_facility_id}`,
  method: METHOD_DELETE,
});

export const DeleteCustomerDialogFormContext = createContext({} as DeleteCustomerDialogForm);

export const DeleteCustomerDialogFormProvider: FC<DeleteCustomerDialogProps> = ({
  children,
  onSuccess,
  customerName,
}) => {
  const { apiRequest } = useAPIActions();
  const { t } = useTranslation();
  const { locationId, customerId } = useParams<{ locationId: string; customerId: string }>();
  const { fetch: refetchCustomers } = useCustomersSettingsPageDataContext();

  const handleDeleteCustomer = useCallback(async () => {
    if (customerId) {
      await apiRequest(API_DELETE_SubFacility(locationId, customerId), {
        onSuccess: () => {
          toastService.success(t('location_settings:DELETE_CUSTOMER_SUCCESS'));
          refetchCustomers();
          onSuccess();
        },
        onError: handleGenericErrorMessage,
      });
    } else {
      onSuccess();
    }
  }, [apiRequest, onSuccess, locationId, customerId, t, refetchCustomers]);

  const contextValue = useMemo(
    () => ({
      handleDeleteCustomer,
      customerName,
    }),
    [handleDeleteCustomer, customerName],
  );

  return (
    <DeleteCustomerDialogFormContext.Provider value={contextValue}>
      {children}
    </DeleteCustomerDialogFormContext.Provider>
  );
};

export const useDeleteCustomerDialogFormContext = () => useContext(DeleteCustomerDialogFormContext);
